'use client';

import { Link } from '@tanstack/react-router';

import { useGetContent } from '@/lib/react-query/ContentQueries';
import { useGetHeroByType } from '@/lib/react-query/HeroQueries';
import { cn } from '@/lib/utils';
import type { HeroImage } from '@/types/content.types';

import Button from '../Button/Button';
import HeroImages from '../HeroImages';
import Icon from '../Icon/Icon';

type Props = {
  className?: string;
  type?: 'piercing' | 'tattoo';
  images?: Array<HeroImage>;
};

const Hero = ({ className = '', type = 'piercing', images }: Props) => {
  const { data: hero } = useGetHeroByType(type);
  const { data: content_data } = useGetContent();
  const instagram = content_data?.instagram?.find((insta) => insta.type === type) || null;

  const buttonText = type === 'tattoo' ? 'Discuter de votre projet' : 'Prendre rendez-vous';
  const buttonLink = type === 'tattoo' ? '/tatouage/projet' : '/rendez-vous';

  const gradientColors =
    type === 'tattoo' ? 'from-emerald-400 to-emerald-600' : 'from-[#80caff] to-[#4f46e5]';

  return (
    <div className={cn('relative isolate overflow-hidden', className)}>
      <div
        className="absolute top-10 left-[calc(50%-4rem)] -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:top-[calc(50%-30rem)] lg:left-48 xl:left-[calc(50%-24rem)]"
        aria-hidden="true"
      >
        <div
          className={cn('aspect-1108/632 w-69.25 bg-linear-to-r opacity-20', gradientColors)}
          style={{
            clipPath:
              'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
          }}
        />
      </div>
      <div className="mx-auto max-w-7xl px-6 pt-16 pb-32 lg:px-8">
        <div className="mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center">
          <div className="relative w-full max-w-xl lg:shrink-0 xl:max-w-2xl">
            <h1 className="mt-10 text-4xl font-bold tracking-tight text-black sm:text-6xl">
              {hero?.title}
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-800">{hero?.description}</p>
            <div className="mt-10 flex items-center gap-x-6">
              <Link to={buttonLink}>
                <Button
                  size="xl"
                  trackEvent={type === 'tattoo' ? 'ProjetClick' : 'RendezVousClick'}
                  trackProps={{ location: 'hero' }}
                  className={type === 'tattoo' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  {buttonText}
                </Button>
              </Link>

              {instagram?.id && instagram.url ? (
                <a
                  href={instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex gap-2 rounded-md border py-2.5 pr-3.5 pl-2 text-sm leading-6 font-semibold transition-all',
                    type === 'tattoo'
                      ? 'border-emerald-900 text-emerald-900 hover:border-emerald-700 hover:text-emerald-700'
                      : 'border-amber-900 text-amber-900 hover:border-amber-700 hover:text-amber-700',
                  )}
                >
                  <Icon name="InstagramIcon" />
                  <span>Instagram</span>
                </a>
              ) : null}
            </div>
          </div>
          <HeroImages type={type} images={images} />
        </div>
      </div>
    </div>
  );
};

export default Hero;
